<template>
    <custom-file-manager
        ref="fileManagerComponent"
        :items="directoryTable.data"
        :onDirectoryChanged="onDirectoryChanged"
        :onDirectoryCreating="onDirectoryCreating"
        :onDirectoryDeleting="onDirectoryDeleting"
        :onDirectoryRenamed="onDirectoryRenamed"
        :onDirectoryMoving="onDirectoryMoving"
        :onFileDeleting="onFileDeleting"
        :onFileOpened="onFileOpened"
        :onFileRenamed="onFileRenamed"
        :onFileMoving="onFileMoving"
        :onFileUploadEvent="onFileUploadEvent"
        :onExcelExportEvent="onExcelExportEvent"
        :excel-export="true"
        :onRefreshEvent="loadDirectoryTree"
        :customizeIcon="onCustomizeIcon"
        :customizeColumn="onCustomizeColumn"
        height="calc(100vh - 250px)"
    ></custom-file-manager>

    <div class="modal fade" id="kt_modal_video" ref="videoModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header" id="kt_modal_add_customer_header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div id="kt_modal_add_customer_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onVideoSubmit()" :model="form.data" ref="videoForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="scroll-y me-n7 pe-7" id="kt_modal_add_customer_scroll" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_add_customer_header" data-kt-scroll-wrappers="#kt_modal_add_customer_scroll" data-kt-scroll-offset="300px">
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.video.upload.cols.video.title') }}</label>
                                <el-form-item>
                                    <el-upload class="w-100" :file-list="form.uploadList" accept="video/*" multiple :show-file-list="false" :on-change="handleChangeFile" :on-remove="handleChangeFile" :auto-upload="false" :limit="100">
                                        <el-button type="primary">{{ $t('common.chooseFile') }}</el-button>
                                    </el-upload>

                                    <div v-if="form.uploadList.length" class="w-100 mt-4">
                                        <div class="d-flex align-items-center justify-content-between mb-3">
                                            <span class="fs-7 fw-semibold text-gray-700">
                                                {{ sprintf($t('pages.video.upload.filesSelected'), [form.uploadList.length]) }}
                                                <span class="text-gray-400 mx-1">·</span>
                                                <span class="text-gray-500">{{ sprintf($t('pages.video.upload.totalSize'), [uploadTotalSize]) }}</span>
                                            </span>
                                            <el-button v-if="!form.loading" type="danger" link size="small" @click="clearUploadList">
                                                {{ $t('pages.video.upload.clearAll') }}
                                            </el-button>
                                        </div>

                                        <div class="upload-file-list">
                                            <div v-for="(file, fileIndex) in form.uploadList" :key="file.uid ?? fileIndex" class="upload-file-item d-flex align-items-center position-relative px-3">
                                                <span class="badge badge-circle badge-light-primary flex-shrink-0 me-3">{{ fileIndex + 1 }}</span>

                                                <el-input v-model="file.name" size="small" :disabled="form.loading" type="text" class="flex-grow-1"/>

                                                <span class="upload-file-size text-gray-400 fs-8 text-nowrap text-start flex-shrink-0 ms-3 d-none d-sm-block">{{ formatBytes(file.size || (file.raw && file.raw.size)) }}</span>

                                                <span v-if="file.uploadStatus" class="badge flex-shrink-0 ms-3" :class="uploadStatusClass(file.uploadStatus)">
                                                    {{ uploadStatusText(file.uploadStatus) }}
                                                </span>

                                                <button v-if="!form.loading" type="button" class="btn btn-icon btn-sm rounded-circle w-25px h-25px flex-shrink-0 ms-3 upload-file-remove" @click="removeUploadFile(fileIndex)">
                                                    <i class="bi bi-x fs-3"></i>
                                                </button>

                                                <div v-if="file.uploadStatus === 'uploading'" class="upload-file-progress" :style="{ width: (file.uploadProgress || 0) + '%' }"></div>
                                            </div>
                                        </div>
                                    </div>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.video.upload.cols.group') }}</label>
                                <el-form-item prop="group_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.group_id" :placeholder="$t('common.chooseSelect')" filterable class="w-100">
                                        <el-option v-for="(group, groupIndex) in videoGroups" :key="groupIndex" :value="group.id" :label="group.name"></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.video.upload.cols.option') }}</label>
                                <el-form-item>
                                    <div class="lh-1 form-check form-check-custom form-check-solid my-2">
                                        <input class="form-check-input" type="checkbox" v-model="form.data.convert_hls" id="exportHLS">
                                        <label class="form-check-label" for="exportHLS">
                                            {{ $t('pages.video.upload.option.convertHls') }}
                                        </label>
                                    </div>
                                    <div v-if="form.enterFolderAvailable" class="lh-1 form-check form-check-custom form-check-solid my-2">
                                        <input class="form-check-input" type="checkbox" v-model="form.enterUploadedFolder" id="enterUploadedFolder">
                                        <label class="form-check-label" for="enterUploadedFolder">
                                            {{ $t('pages.video.upload.option.enterUploadedFolder') }}
                                        </label>
                                    </div>
                                </el-form-item>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomFileManager from "@/components/custom-file-manager";
import excelExportLoadingOptions, {excelExportFallbackName, excelExportAlertOptions} from "@/core/helpers/excelExportAnimation";

export default {
    name: "index",
    components: {
        CustomFileManager
    },
    data() {
        return {
            directoryTable: {
                data: [],
                loading: false,
            },
            form: {
                title: '',
                uploadList: [],
                loading: false,
                updateStatus: false,
                enterFolderAvailable: false,
                enterUploadedFolder: false,
                targetPath: '',
                data: {}
            },
            filesPath: 'media/svg/files',
            UPLOAD_CONCURRENCY: 3
        }
    },
    created() {
        this.loadDirectoryTree();
    },
    computed: {
        videoGroups() {
            return this.$store.state.video.group.table.data;
        },
        uploadTotalSize() {
            let bytes = this.form.uploadList.reduce((sum, file) => {
                return sum + (file.size || (file.raw && file.raw.size) || 0);
            }, 0);

            return this.formatBytes(bytes);
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("pages.directory.title"));
        this.$store.dispatch('video/group/get', {
            page: {pageSize: 9999}
        });
    },
    methods: {
        loadDirectoryTree() {
            this.directoryTable.loading = true;

            this.axios.get(this.endpoint.get('directory_tree')).then((response) => {
                let data = response.data.data;
                this.directoryTable.data = data;

                this.checkVideoWithoutFolder();
            }).finally(() => {
                this.directoryTable.loading = false;
            });
        },
        onDirectoryChanged(e) {
            let directory = e.directory;
            let dataItem = directory.dataItem ?? undefined;

            if (dataItem && dataItem.videos_count > 0 && !dataItem.childrenLoaded) {
                this.axios.get(this.endpoint.get('video'), {
                    params: {
                        directory_id: dataItem.id,
                        sort: 'created_at:desc',
                        page_limit: 1000
                    }
                }).then((response) => {
                    let data = response.data.data;

                    if (!dataItem.children) {
                        dataItem.children = [];
                    }

                    data.forEach((video) => {
                        video.is_directory = false;
                        dataItem.children.push(video);
                    });

                    dataItem.childrenLoaded = true;

                    this.refreshFileManager();
                });
            }
        },
        onDirectoryCreating(e) {
            let directoryItem = e.parentDirectory.dataItem;

            if (directoryItem && directoryItem.id == 0) {
                e.errorText = this.$t('pages.directory.warnings.cantCreatedWithoutFolder');
                e.cancel = true;

                return;
            }

            this.form.data = {
                parent_id: (directoryItem && directoryItem.id ? directoryItem.id : undefined),
                title: e.name
            };

            this.directoryRequest(() => {
                this.loadDirectoryTree();
            });
        },
        directoryRequest(successEvent = undefined) {
            let axios = null;
            if (this.form.data.id) {
                axios = this.axios.put(this.endpoint.get('directory') + '/' + this.form.data.id, this.form.data);
            } else {
                axios = this.axios.post(this.endpoint.get('directory'), this.form.data);
            }

            if (axios) {
                axios.then(response => {
                    this.onResponse(response.data, successEvent, () => {
                        this.loadDirectoryTree();
                    }, false, false);
                }).catch(error => {
                    this.onResponseFailure(error.response.data, () => {
                        this.loadDirectoryTree();
                    });
                });
            }
        },
        onDirectoryDeleting(e, data) {
            if (data.id == 0) {
                e.errorText = this.$t('pages.directory.warnings.cantDeletedWithoutFolder');
                e.cancel = true;

                return;
            }

            this.$store.dispatch("directory/delete", {
                id: [data.id]
            }).then((successDeleted) => {
                if (!successDeleted) {
                    this.loadDirectoryTree();
                }
            });
        },
        onDirectoryRenamed(name, data) {
            this.form.data = {
                id: data.id,
                title: name,
            };

            this.directoryRequest();
        },
        onDirectoryMoving(e, item, destinationDirectory) {
            if (destinationDirectory.id == 0) {
                e.errorText = this.$t('pages.directory.warnings.cantMovedFolderToWithoutFolder');
                e.cancel = true;

                return;
            }

            this.form.data = {
                id: item.id,
                parent_id: destinationDirectory ? destinationDirectory.id : null,
            };

            this.directoryRequest();
        },
        onFileDeleting(e, data) {
            this.$store.dispatch("video/delete", {
                id: [data.id]
            }).then((successDeleted) => {
                if (!successDeleted) {
                    this.loadDirectoryTree();
                }
            });
        },
        onFileOpened(file) {
            this.trustedWindowOpen(this.sprintf('/video/detail/%d', [file.dataItem.id]), '_blank')
        },
        onFileRenamed(name, data) {
            this.form.data = {
                id: data.id,
                title: name,
            };

            this.videoRequest();
        },
        onFileMoving(e, item, destinationDirectory) {
            if (!destinationDirectory) {
                e.errorText = this.$t('pages.directory.warnings.cantMovedHomeDirectory');
                e.cancel = true;

                return;
            }

            if (destinationDirectory.id == 0) {
                e.errorText = this.$t('pages.directory.warnings.cantMovedFileToWithoutFolder');
                e.cancel = true;

                return;
            }

            this.form.data = {
                id: item.id,
                directory_id: destinationDirectory.id,
            };

            this.videoRequest();
        },
        onFileUploadEvent(event, targetDirectory) {
            let dataItem = targetDirectory.dataItem;

            if (!this.directoryTable.data.length || !dataItem || dataItem.id == 0) {
                this.$swal.fire({
                    icon: 'warning',
                    title: this.$t('messages.warning'),
                    text: this.$t('pages.directory.warnings.requiredDirectory'),
                    showCancelButton: true,
                    showConfirmButton: false,
                    cancelButtonText: this.$t('btn.close')
                });
                return;
            }

            let currentDir = this.$refs.fileManagerComponent.fileManager.getCurrentDirectory();
            let currentPath = currentDir ? (currentDir.path || '') : '';
            let targetPath = targetDirectory.path || '';

            this.form.uploadList = [];
            this.form.enterFolderAvailable = (targetPath !== currentPath);
            this.form.enterUploadedFolder = false;
            this.form.targetPath = targetPath;

            this.form.data = {
                convert_unsupported: true,
                convert_hls: true,
                directory_id: dataItem.id
            };
            this.form.title = this.$t("pages.video.upload.title");
            this.showModal(this.$refs.videoModal);
        },
        onExcelExportEvent(e, targetDirectory, selectedItems) {
            let dataItem = targetDirectory ? targetDirectory.dataItem : undefined;

            // Checkbox selection wins: every selected folder and video goes
            // into a single export (folders expand to their subtrees below)
            let items = (selectedItems || []);
            let selectedDirectoryIds = items.filter((item) => item.isDirectory && item.dataItem && item.dataItem.id).map((item) => item.dataItem.id);
            let selectedVideoIds = items.filter((item) => !item.isDirectory && item.dataItem && item.dataItem.id).map((item) => item.dataItem.id);

            if (selectedVideoIds.length && !selectedDirectoryIds.length) {
                // Only videos selected: no subfolder question, sorting still applies
                this.askExportScope(
                    this.sprintf(this.$t('pages.directory.export.selectedVideoSummary'), [selectedVideoIds.length]),
                    (includeChildren, sort) => this.downloadDirectoryExport({
                        video_ids: selectedVideoIds,
                        sort: sort
                    }),
                    false
                );
                return;
            }

            if (selectedDirectoryIds.length) {
                this.askExportScope(
                    this.sprintf(this.$t('pages.directory.export.selectedSummary'), [selectedDirectoryIds.length, selectedVideoIds.length]),
                    (includeChildren, sort) => this.downloadDirectoryExport({
                        directory_ids: selectedDirectoryIds,
                        video_ids: (selectedVideoIds.length ? selectedVideoIds : undefined),
                        include_children: (includeChildren ? 1 : 0),
                        sort: sort
                    })
                );
                return;
            }

            if (!dataItem || !dataItem.id) {
                this.$swal.fire(excelExportAlertOptions('warning', this.$t('messages.warning'), this.$t('pages.directory.warnings.exportRequiredDirectory'), this.$t('btn.close')));
                return;
            }

            this.askExportScope(dataItem.title, (includeChildren, sort) => this.downloadDirectoryExport({
                directory_ids: [dataItem.id],
                include_children: (includeChildren ? 1 : 0),
                sort: sort
            }));
        },
        askExportScope(title, onConfirm, showScope = true) {
            let safeTitle = String(title).replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));

            let sortOptionsHtml = [
                {value: 'title:asc', label: this.$t('sortFields.video.titleAsc')},
                {value: 'title:desc', label: this.$t('sortFields.video.titleDesc')},
                {value: 'created_at:asc', label: this.$t('sortFields.video.createdAtAsc')},
                {value: 'created_at:desc', label: this.$t('sortFields.video.createdAtDesc')},
                {value: 'duration:asc', label: this.$t('sortFields.video.durationAsc')},
                {value: 'duration:desc', label: this.$t('sortFields.video.durationDesc')},
                {value: 'size:asc', label: this.$t('sortFields.video.sizeAsc')},
                {value: 'size:desc', label: this.$t('sortFields.video.sizeDesc')},
                {value: 'quality:asc', label: this.$t('sortFields.video.qualityAsc')},
                {value: 'quality:desc', label: this.$t('sortFields.video.qualityDesc')}
            ].map((option) => `<option value="${option.value}"${option.value === 'title:asc' ? ' selected' : ''}>${option.label}</option>`).join('');

            // Scope and in-folder sorting as custom radio groups (sweetalert2
            // v9 supports a single native input, so both live in our own html)
            this.$swal.fire({
                icon: 'question',
                html: `
<style>
.excel-scope-popup { width: 37em !important; max-width: 94vw !important; }
.excel-scope-popup .swal2-icon { width: 4em; height: 4em; min-width: 4em; margin: .25rem auto .7rem !important; }
.excel-scope-popup .swal2-icon .swal2-icon-content { font-size: 2.9em; }
.excel-scope-popup .swal2-content { margin-top: 0 !important; }
.excel-scope-popup .swal2-actions { margin: .9rem auto .25rem !important; }
.excel-scope-popup .excel-scope-title { font-size: 1.25rem; font-weight: 500; color: #181c32; margin-bottom: .5rem; }
.excel-scope-popup .excel-scope-question { font-size: 1.02rem; color: #7e8299; }
.excel-scope-popup .excel-scope-options + .excel-scope-question { margin-top: 1rem; }
.excel-scope-popup .excel-scope-options { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin: .6em auto 0; }
.excel-scope-popup .excel-scope-options label { display: flex; align-items: center; gap: 9px; margin: 0; padding: 10px 15px; background: #f5f8fa; border: 2px solid #f5f8fa; border-radius: 12px; cursor: pointer; transition: border-color .15s ease, background .15s ease; }
.excel-scope-popup .excel-scope-options label:hover { border-color: #b6dffc; }
.excel-scope-popup .excel-scope-options label:has(input:checked) { background: rgba(0, 158, 247, .08); border-color: #009ef7; }
.excel-scope-popup .excel-scope-options label:has(input:checked) span { color: #009ef7; }
.excel-scope-popup .excel-scope-options input { appearance: none; -webkit-appearance: none; width: 18px; height: 18px; margin: 0; border: 2px solid #c9ced6; border-radius: 50%; background: #fff; transition: border .15s ease; flex-shrink: 0; }
.excel-scope-popup .excel-scope-options input:checked { border: 5px solid #009ef7; }
.excel-scope-popup .excel-scope-options span { font-size: .92rem; font-weight: 600; color: #3f4254; white-space: nowrap; transition: color .15s ease; }
.excel-scope-popup .excel-scope-select { display: block; width: 78%; margin: .6em auto 0; padding: 10px 42px 10px 16px; background: #f5f8fa url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%237e8299'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E") no-repeat right 16px center/14px; border: 2px solid #f5f8fa; border-radius: 12px; font-size: .95rem; font-weight: 600; color: #3f4254; cursor: pointer; appearance: none; -webkit-appearance: none; transition: border-color .15s ease; }
.excel-scope-popup .excel-scope-select:hover { border-color: #b6dffc; }
.excel-scope-popup .excel-scope-select:focus { outline: 0; border-color: #009ef7; }
</style>
<div class="excel-scope-title">${safeTitle}</div>
${showScope ? `<div class="excel-scope-question">${this.$t('pages.directory.export.includeChildrenQuestion')}</div>
<div class="excel-scope-options">
    <label><input type="radio" name="excelScopeChildren" value="children" checked><span>${this.$t('pages.directory.export.withChildren')}</span></label>
    <label><input type="radio" name="excelScopeChildren" value="single"><span>${this.$t('pages.directory.export.onlyThis')}</span></label>
</div>` : ''}
<div class="excel-scope-question">${this.$t(showScope ? 'pages.directory.export.sortQuestion' : 'pages.directory.export.sortQuestionVideos')}</div>
<select name="excelScopeSort" class="excel-scope-select">${sortOptionsHtml}</select>
`,
                customClass: {
                    popup: 'excel-scope-popup'
                },
                showCancelButton: true,
                confirmButtonText: this.$t('common.excelExport'),
                cancelButtonText: this.$t('btn.cancel'),
                preConfirm: () => {
                    let popup = this.$swal.getPopup();

                    return {
                        children: ((popup.querySelector('input[name="excelScopeChildren"]:checked') || {}).value !== 'single'),
                        sort: ((popup.querySelector('select[name="excelScopeSort"]') || {}).value || 'title:asc')
                    };
                }
            }).then((result) => {
                if (result.isConfirmed && result.value) {
                    onConfirm(result.value.children, result.value.sort);
                }
            });
        },
        downloadDirectoryExport(params) {
            this.$swal.fire(excelExportLoadingOptions(this.$t('pages.directory.export.preparing')));

            this.axios.get(this.endpoint.get('directory_export_videos'), {
                params: params,
                responseType: 'blob'
            }).then((response) => {
                let fileName = excelExportFallbackName();
                let disposition = response.headers['content-disposition'];

                if (disposition) {
                    let matches = disposition.match(/filename="?([^";]+)"?/);

                    if (matches) {
                        fileName = matches[1];
                    }
                }

                let url = window.URL.createObjectURL(new Blob([response.data], {
                    type: response.headers['content-type']
                }));

                let link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);

                this.$swal.close();
            }).catch(() => {
                this.$swal.fire(excelExportAlertOptions('error', this.$t('messages.error'), this.$t('pages.directory.export.failed'), this.$t('btn.close')));
            });
        },
        onCustomizeIcon(fileSystemItem) {
            let dataItem = fileSystemItem.dataItem;

            if (fileSystemItem.isDirectory) {
                return this.filesPath + "/folder.svg";
            } else {
                if (dataItem && dataItem.image) {
                    return dataItem.image.direct_link;
                } else {
                    return this.filesPath + "/video.svg";
                }
            }
        },

        onCustomizeColumn(columns) {
            columns[0].width = 45;
            delete columns[2].hidingPriority;
            delete columns[3].hidingPriority;

            columns.splice(2, 0, {
                caption: this.$t('pages.directory.videoCount'),
                width: 80,
                dataField: 'videos_count',
                name: 'videos_count',
            });

            return columns;
        },
        refreshFileManager() {
            this.$refs.fileManagerComponent.fileManager.refresh();
        },
        videoRequest(successEvent = undefined) {
            let axios = null;
            if (this.form.data.id) {
                axios = this.axios.put(this.endpoint.get('video') + '/' + this.form.data.id, this.form.data);
            } else {
                axios = this.axios.post(this.endpoint.get('video'), this.form.data);
            }

            if (axios) {
                axios.then(response => {
                    this.onResponse(response.data, successEvent, () => {
                        this.loadDirectoryTree();
                    }, false, false);
                }).catch(error => {
                    this.onResponseFailure(error.response.data, () => {
                        this.loadDirectoryTree();
                    });
                });
            }
        },
        onVideoSubmit() {
            this.$refs.videoForm.validate(async (valid) => {
                if (!valid) {
                    return false;
                }

                if (!(this.form.uploadList.length > 0)) {
                    this.$notify({
                        type: 'warning',
                        title: this.$t("messages.warning"),
                        message: this.$t("pages.video.upload.warnings.requiredFile"),
                    })
                    return false;
                }

                this.form.loading = true;

                let pending = this.form.uploadList.filter((file) => file.uploadStatus !== 'success');

                pending.forEach((file) => {
                    file.uploadStatus = 'pending';
                    file.uploadProgress = 0;
                });

                let results = await this.runUploadQueue(pending, this.UPLOAD_CONCURRENCY);
                let failed = results.filter((result) => !result.success);

                this.form.loading = false;

                if (this.form.enterUploadedFolder && this.form.targetPath) {
                    this.$refs.fileManagerComponent.navigateToPathAfterReload(this.form.targetPath);
                }

                this.loadDirectoryTree();

                if (failed.length === 0) {
                    this.$notify({
                        type: 'success',
                        title: this.$t("messages.success"),
                        message: this.sprintf(this.$t("pages.video.upload.messages.allSuccess"), [pending.length]),
                    });
                    this.hideModal(this.$refs.videoModal);
                } else {
                    this.$notify({
                        type: 'error',
                        title: this.$t("messages.error"),
                        message: this.sprintf(this.$t("pages.video.upload.messages.partialFail"), [failed.length]),
                    });
                }
            });
        },
        async runUploadQueue(files, concurrency) {
            let results = new Array(files.length);
            let cursor = 0;

            let worker = async () => {
                while (cursor < files.length) {
                    let index = cursor++;
                    results[index] = await this.uploadSingleFile(files[index]);
                }
            };

            let pool = Array.from(
                {length: Math.min(concurrency, files.length)},
                () => worker()
            );

            await Promise.all(pool);

            return results;
        },
        async uploadSingleFile(file) {
            file.uploadStatus = 'uploading';
            file.uploadProgress = 0;

            const formData = new FormData();
            formData.append("file", file.raw);
            formData.append("title", file.name);

            for (let key in this.form.data) {
                formData.append(key, (typeof (this.form.data[key]) == 'boolean' ? (Number(this.form.data[key])) : this.form.data[key]));
            }

            try {
                let response = await this.axios.post(this.endpoint.get('video'), formData, {
                    onUploadProgress: (event) => {
                        if (event.total) {
                            file.uploadProgress = Math.round((event.loaded * 100) / event.total);
                        }
                    }
                });

                if (response.data && response.data.status) {
                    file.uploadStatus = 'success';
                    file.uploadProgress = 100;

                    return {success: true, data: response.data.data};
                }

                file.uploadStatus = 'error';
                return {success: false};
            } catch (err) {
                file.uploadStatus = 'error';

                this.axios.post(this.endpoint.get('log_register', 'system'), {
                    error: err.response,
                    upload_content: {name: file.name, size: file.size}
                });

                return {success: false};
            }
        },
        uploadStatusText(status) {
            return {
                pending: this.$t('pages.video.upload.status.pending'),
                uploading: this.$t('pages.video.upload.status.uploading'),
                success: this.$t('pages.video.upload.status.success'),
                error: this.$t('pages.video.upload.status.error'),
            }[status] || '';
        },
        uploadStatusClass(status) {
            return {
                pending: 'badge-light-secondary',
                uploading: 'badge-light-primary',
                success: 'badge-light-success',
                error: 'badge-light-danger',
            }[status] || 'badge-light';
        },
        removeUploadFile(index) {
            this.form.uploadList.splice(index, 1);
        },
        clearUploadList() {
            this.form.uploadList = [];
        },
        formatBytes(bytes) {
            if (!bytes) {
                return '0 B';
            }

            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        handleChangeFile(file, fileList) {
            this.form.uploadList = fileList;
        },
        checkVideoWithoutFolder() {
            this.axios.get(this.endpoint.get('video'), {
                params: {
                    directory_id: 0,
                    sort: 'created_at:desc',
                    page_limit: 1000
                }
            }).then((response) => {
                let data = response.data.data.map((video) => {
                    video.is_directory = false;

                    return video;
                });

                let meta = response.data.meta;

                if (data.length) {
                    this.directoryTable.data.unshift({
                        id: 0,
                        is_directory: true,
                        title: this.$t("pages.directory.videoWithoutFolder"),
                        children: data,
                        childrenLoaded: true,
                        videos_count: meta.total_record
                    });

                    setTimeout(() => {
                        this.refreshFileManager();
                    }, 0);
                }
            });
        },
    }
}
</script>

<style>
.dx-filemanager .dx-filemanager-files-view.dx-filemanager-details .dx-filemanager-details-item-thumbnail {
    width: 30px;
    height: 30px;
}

.dx-datagrid-content .dx-datagrid-table .dx-row > td, .dx-datagrid-content .dx-datagrid-table .dx-row > tr > td {
    vertical-align: middle;
}

.upload-file-list {
    max-height: 340px;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #eff2f5;
    border-radius: .475rem;
}

.upload-file-item {
    height: 56px;
    border-bottom: 1px solid #f4f6f8;
    transition: background-color .15s ease;
}

.upload-file-item:last-child {
    border-bottom: none;
}

.upload-file-item:hover {
    background-color: #f9fafb;
}

.upload-file-size {
    width: 72px;
}

.upload-file-remove {
    background-color: #fbd9e1 !important;
    transition: background-color .15s ease, color .15s ease;
}

.upload-file-remove i {
    color: #f1416c !important;
}

.upload-file-remove:hover {
    background-color: var(--bs-danger, #f1416c) !important;
}

.upload-file-remove:hover i {
    color: #fff !important;
}

.upload-file-progress {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background-color: var(--bs-primary, #009ef7);
    transition: width .25s ease;
}
</style>